import React, { useState, useEffect } from "react";
import { Button, FormGroup, Typography, Dialog, Box, CircularProgress, DialogTitle, DialogContent, DialogContentText, DialogActions, Divider } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import userSupportService from "../../../services/UserSupportService";
import {
  AcademicYearDTO,
  AssessmentType,
  LookupUserDTOV1,
  ResettableAssessment,
} from "@beable/roster-backend-client-v1";
import { getStyles } from "./styles";
import { emptyUserProfile } from '../fixtures';
import moment from "moment";
import LanguageToggle from "../LanguageToggle";
import { Languages } from "@beable/content-types";

type ResetAssessmentModalProps = {
  profile: LookupUserDTOV1;
  open: boolean;
  handleClose: () => void;
};

export default function ResetAssessmentModal(props: ResetAssessmentModalProps) {
  const theme = useTheme();
  const styles = getStyles(theme);

  const [profile, setProfile] = useState(emptyUserProfile);
  const [hasErr, setHasErr] = useState(false);
  const [hasSuccess, setSuccess] = useState(false);
  const [assessments, setAssessments] = useState<
    ResettableAssessment[] | undefined
  >();
  const [academicYear, setAcademicYear] = useState<AcademicYearDTO | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [languageCode, setLanguageCode] = useState<string>(Languages.English);
  const [confirmResetOpen, setConfirmResetOpen] = useState<boolean>(false);


  useEffect(() => {
    const getAssessment = async () => {
      if (!languageCode) {
        return;
      }

      const { assessments, academicYear } = await userSupportService.getResettableAssessments(
        props.profile.userAccountId!,
        props.profile.userId,
        props.profile.siteId,
        languageCode
      );
      setAssessments(assessments);
      setAcademicYear(academicYear);
    };

    setProfile(props.profile);
    getAssessment();
  }, [props.profile, languageCode]);

  const onToggleLanguage = (language: string) => {
    if (language !== null) {
      setLanguageCode(language);
    }
  }

  const resetAssessments = async () => {
    setIsSubmitting(true);
    closeResetConfirmDialog();
    setHasErr(false);
    const success = await userSupportService.resetAssessments(
        props.profile.userAccountId!,
        props.profile.siteId,
        languageCode,
        assessments!,
        academicYear!
    );

    if (success) {
      setSuccess(true);
    } else {
      setHasErr(true);
    }
    setIsSubmitting(false);
  };

  const closeResetConfirmDialog = () => {
    setConfirmResetOpen(false);
  };

  const confirmReset = (event: any) => {
    event.preventDefault();
    setConfirmResetOpen(true); 
  }

  const resetState = async () => {
    setSuccess(false);
    setHasErr(false);
    setAssessments(undefined);
  };

  const isDisabled = () => {
    return !assessments?.length || isSubmitting;
  }

  const formatAssessmentType = (assessmentType: AssessmentType) => {
    return AssessmentType[assessmentType].replace(/_/g, ' ').toLowerCase().replace(/^\w|\s\w/g, c => c.toUpperCase());
  }

  const getAssessmentDisplay = (assessments: ResettableAssessment[]) => {
    const display: ResettableAssessment[] = [];
    for (const assessment of assessments) {
      if (assessment.rootAssessmentId === assessment.assessmentId) {
        const finalAssessment = assessments.find(a => a.rootAssessmentId === assessment.assessmentId && !!a.completedAt);
        display.push({
          ...assessment,
          completedAt: finalAssessment ? finalAssessment.completedAt : assessment.completedAt,
          lexileScore: finalAssessment ? finalAssessment.lexileScore : assessment.lexileScore
        });
      }
    }
    return display;
  }
  
  return <>
  <Dialog
    aria-labelledby="admin-modal-title"
    aria-describedby="admin-modal-description"
    open={props.open}
    onClose={(_event, _reason) => {
        props.handleClose();
    }}
    scroll="body"
  >
    <form onSubmit={confirmReset}>
      <Box sx={styles.paper}>
        <h2 id="admin-modal-title">Reset Assessment</h2>
        <Typography variant="body2">
          This tool will reset all assessments for the selected language and current academic year.
        </Typography>
        <FormGroup sx={[styles.mt2, styles.dfr]}>
          {assessments?.length ?
            (<Typography sx={[styles.mt2, styles.mb2]}>
              For security purposes, before resetting the assessment, please
              confirm the user's information shown below.
            </Typography>)
          : undefined
          }
          <Box sx={styles.box}>
            Language:
            <LanguageToggle
              onToggle={onToggleLanguage}
              selectedLanguage={languageCode}
              showBorders
            />
          </Box>
          {assessments?.length ? (
            <>
              <Box sx={{ ...styles.box, width: '100%' }}>
                Name: {profile.lastName}, {profile.firstName} <br />
                { getAssessmentDisplay(assessments)
                  .sort((a, b) => {
                    return new Date(a.completedAt!).getTime() - new Date(b.completedAt!).getTime();
                  })
                  .map((assessment) => (
                  <React.Fragment key={assessment.assessmentId}>
                    <Divider sx={{ border: '1px solid #ccc', margin: '10px 0' }} />
                    Assessment Type: {formatAssessmentType(assessment.assessmentType)} <br />
                    Assessment Score: {assessment.lexileScore} <br />
                    Completed On: { assessment.completedAt ? moment(assessment.completedAt).format("YYYY-MM-DD") : "Incomplete" }
                  </React.Fragment>
                ))}
                <Divider sx={{ border: '1px solid #ccc', margin: '10px 0' }} />
              </Box>
              <Box sx={{ width: "100%" }}>
                {hasErr ? (
                  <Typography sx={[styles.redTxt, styles.mb2]}>
                    Assessment reset failed!
                  </Typography>
                ) : undefined}
                {hasSuccess ? (
                  <Typography
                    sx={[styles.greenTxt, styles.mb2]}
                  >
                    Assessment reset successful!
                  </Typography>
                ) : undefined}
              </Box>
            </>
          ) : (
            <Typography sx={[styles.redTxt, styles.mb2]}>
              This user has not completed any { languageCode ? Object.keys(Languages)[Object.values(Languages).indexOf(languageCode as Languages)] : '' } assessments
            </Typography>
          )}
          <Button
            variant="contained"
            color="secondary"
            type="submit"
            disabled={isDisabled()}
          >
            {isSubmitting && (
                <>
                  <CircularProgress data-testid="progress" size={8} />
                  &nbsp;
                </>
              )}
            Reset Assessment
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              resetState();
              props.handleClose();
            }}
            sx={styles.ml2}>
            Close
          </Button>
        </FormGroup>
      </Box>
    </form>
  </Dialog>
  <Dialog
      open={confirmResetOpen}
      onClose={closeResetConfirmDialog}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">Important!</DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
        You are about to reset this user's assessment{ (assessments?.length ?? 0) > 1 ? 's' : '' }. This action can not be reversed.  Continue?
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={closeResetConfirmDialog}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={resetAssessments}
          autoFocus
        >
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  </>;
}